import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '../views/Home.vue';
import SignUp from '../views/signUp/SignUpContainer.vue';
import ConfirmationCode from '../views/ConfirmationCode.vue';
import ChangePhone from '../views/ChangePhone.vue';
import CitySelection from '../views/CitySelection.vue';
import CourseSelection from '../views/CourseSelection.vue';
import CampusSelection from '../views/CampusSelection.vue';
import AdmissionTypeSelection from '../views/AdmissionTypeSelection.vue';
import EssayThemeSelection from '@/views/essay/EssayThemeSelection.vue';
import EssayThemeSupportText from '@/views/essay/EssayThemeSupportText.vue';
import EssaySend from '@/views/essay/EssaySend.vue';
import EnemYearSelection from '../views/enem/EnemYearSelection.vue';
import EnemSubscriptionNumber from '../views/enem/EnemSubscriptionNumber.vue';
import EnemGrades from '../views/enem/EnemGrades.vue';
import AdmissionResult from '../views/AdmissionResult.vue';
import TransferGraduationSendDocument from '../views/transferGraduation/TransferGraduationSendDocument.vue';
import SecondGraduationInstitutionInfo from '../views/secondGraduation/SecondGraduationInstitutionInfo.vue';
import TransferGraduationInstitutionInfo from '../views/transferGraduation/TransferGraduationInstitutionInfo.vue';
import SecondGraduationSendDocument from '../views/secondGraduation/SecondGraduationSendDocument.vue';
import pipeline from '@/pipeline';
import stepRouteMapping from '@/pipeline/stepRouteMapping';
import VIEWS_NAMES from './viewsNames';
import { AnalyticsActions } from '@/store/modules/analytics/actions';
import { AdmissionActions } from '@/store/modules/admission/actions';
import { continueMiddleware, hasCityInStoreMiddleware } from '../middleware';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: VIEWS_NAMES.HOME,
    component: Home,
  },
  {
    path: '/comecar',
    name: VIEWS_NAMES.SIGN_UP,
    component: SignUp,
  },
  {
    path: '/continuando',
    name: VIEWS_NAMES.SIGN_UP_OUT_OF_TEST,
    component: SignUp,
    meta: {
      middleware: continueMiddleware,
    },
  },
  {
    path: '/continuar',
    name: VIEWS_NAMES.SIGN_IN,
    redirect: { name: VIEWS_NAMES.SIGN_UP_OUT_OF_TEST },
  },
  {
    path: '/codigo-de-confirmacao',
    name: VIEWS_NAMES.CONFIRMATION_CODE,
    component: ConfirmationCode,
  },
  {
    path: '/troca-celular',
    name: VIEWS_NAMES.CHANGE_PHONE,
    component: ChangePhone,
  },
  { path: '/selecao-de-curso', name: VIEWS_NAMES.COURSE_SELECTION, component: CourseSelection },
  { path: '/selecao-de-cidade', name: VIEWS_NAMES.CITY_SELECTION, component: CitySelection },
  {
    path: '/selecao-de-polo',
    name: VIEWS_NAMES.CAMPUS_SELECTION,
    component: CampusSelection,
    meta: { middleware: hasCityInStoreMiddleware },
  },
  {
    path: '/forma-de-ingresso',
    name: VIEWS_NAMES.ADMISSION_TYPE_SELECTION,
    component: AdmissionTypeSelection,
  },
  {
    path: '/tema-de-redacao',
    name: VIEWS_NAMES.ESSAY_THEME_SELECTION,
    component: EssayThemeSelection,
  },
  {
    path: '/apoio-para-redacao',
    name: VIEWS_NAMES.ESSAY_THEME_SUPPORT_TEXT,
    component: EssayThemeSupportText,
  },
  { path: '/envio-de-redacao', name: VIEWS_NAMES.ESSAY_SEND, component: EssaySend },
  {
    path: '/selecao-de-ano-enem',
    name: VIEWS_NAMES.ENEM_YEAR_SELECTION,
    component: EnemYearSelection,
  },
  {
    path: '/inscricao-enem',
    name: VIEWS_NAMES.ENEM_SUBSCRIPTION_NUMBER,
    component: EnemSubscriptionNumber,
  },
  { path: '/notas-do-enem', name: VIEWS_NAMES.ENEM_GRADES, component: EnemGrades },
  {
    path: '/dados-da-primeira-graduacao',
    name: VIEWS_NAMES.SECOND_GRADUATION_INSTITUTION_INFO,
    component: SecondGraduationInstitutionInfo,
  },
  {
    path: '/dados-de-transferencia',
    name: VIEWS_NAMES.TRANSFER_GRADUATION_INSTITUTION_INFO,
    component: TransferGraduationInstitutionInfo,
  },
  {
    path: '/documentacao-da-primeira-graduacao',
    name: VIEWS_NAMES.SECOND_GRADUATION_SEND_DOCUMENT,
    component: SecondGraduationSendDocument,
  },
  {
    path: '/documentacao-da-transferencia',
    name: VIEWS_NAMES.TRANSFER_GRADUATION_SEND_DOCUMENT,
    component: TransferGraduationSendDocument,
  },
  { path: '/resultado', name: VIEWS_NAMES.ADMISSION_RESULT, component: AdmissionResult },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: () => {
    return { x: 0, y: 0 };
  },
});
interface Context {
  from: any;
  next: Function;
  router: VueRouter;
  to: any;
}

function nextFactory(context: Context, middlewares: Function[], index: number) {
  const subsequentMiddleware = middlewares[index];
  // If no subsequent Middleware exists,
  // the default `next()` callback is returned.
  if (!subsequentMiddleware) return context.next;

  return (...parameters: any[]) => {
    // Run the default Vue Router `next()` callback first.
    context.next(...parameters);
    // Then run the subsequent Middleware with a new
    // `nextMiddleware()` callback.
    const nextMiddleware = nextFactory(context, middlewares, index + 1);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
}

const hasQuery = (route: any) => {
  const queryValues = Object.values(route?.query || {});
  return !!queryValues.length;
};

router.beforeEach((to, from, next) => {
  const doAddQuery = !hasQuery(to) && hasQuery(from);
  if (doAddQuery) {
    return router.push({
      ...to,
      name: to?.name || '',
      query: { ...from.query, ...to.query },
    });
  }
  const isRouteOnStateMachine = Object.values(stepRouteMapping).includes(to.path);

  if (pipeline.initialized) {
    const stepRoute = stepRouteMapping[pipeline.currentStep];

    if (stepRoute === to.path) next();
    else {
      // Delegates back behavior to pipeline
      next(from.path);
      pipeline.prev();
    }
  } else if (isRouteOnStateMachine) {
    // TODO: change to a fallback route
    next('/');
  } else {
    next();
  }
  if (to?.meta?.middleware) {
    const middlewares = Array.isArray(to?.meta?.middleware)
      ? to?.meta?.middleware
      : [to?.meta?.middleware];

    const context = {
      from,
      next,
      router,
      to,
    };
    const nextMiddleware = nextFactory(context, middlewares, 1);

    const [currentMiddleware] = middlewares;

    return currentMiddleware({ ...context, next: nextMiddleware });
  }
});

router.afterEach((to, from) => {
  const { $store } = router.app;

  if (!$store) {
    return;
  }

  $store.dispatch(AnalyticsActions.PAGE_VIEW);
  $store.dispatch(AdmissionActions.CHECK_ADMISSION_AVAILABILITIES);
});

export default router;
